import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, ChevronRight } from 'lucide-react'
import { cn } from '@/utils'
import { useUIStore } from '@/store/uiStore'

interface Ability {
  id: string
  name: string
  description: string
  icon: string
  examples: string[]
  category: string
  voice_triggers: string[]
  action?: () => void  // Optional direct action
}

interface AbilitiesShowcaseProps {
  onTryAbility?: (example: string) => void
  className?: string
}

export function AbilitiesShowcase({ onTryAbility, className }: AbilitiesShowcaseProps) {
  const { setAppearanceModalOpen } = useUIStore()
  
  // Fallback abilities in case API is unavailable
  const FALLBACK_ABILITIES: Ability[] = [
    {
      id: 'uber_rides',
      name: 'Book Uber',
      description: 'Get Uber price estimates and book rides',
      icon: '🚗',
      examples: ['Get me an Uber to JKIA', 'How much is Uber from Westgate to Karen?'],
      category: 'Transport',
      voice_triggers: ['uber', 'get a ride', 'book taxi']
    },
    {
      id: 'airtime',
      name: 'Buy Airtime',
      description: 'Purchase airtime for yourself or others instantly',
      icon: '📱',
      examples: ['Buy me 100 bob airtime', 'Send 500 airtime to 0722000000'],
      category: 'Utilities',
      voice_triggers: ['buy airtime', 'top up']
    },
    {
      id: 'movie_streaming',
      name: 'Watch Movies',
      description: 'Find free movies on streaming platforms',
      icon: '🎬',
      examples: ['Where can I watch The Matrix for free?', 'Find free action movies'],
      category: 'Entertainment',
      voice_triggers: ['watch movie', 'find movie']
    },
    {
      id: 'create_video',
      name: 'Create Videos',
      description: 'Generate short videos for social media',
      icon: '🎥',
      examples: ['Create a promo video', 'Make a TikTok-style video'],
      category: 'Creative',
      voice_triggers: ['create video', 'make video']
    },
    {
      id: 'email',
      name: 'Email Assistant',
      description: 'Read and manage your emails',
      icon: '📧',
      examples: ['Check my inbox', 'Do I have any new emails?'],
      category: 'Productivity',
      voice_triggers: ['check email', 'my emails']
    },
    {
      id: 'web_search',
      name: 'Search the Web',
      description: 'Find information from the internet',
      icon: '🔍',
      examples: ["What's the latest news?", 'Search for best restaurants'],
      category: 'Research',
      voice_triggers: ['search for', 'look up']
    },
    {
      id: 'get_trading_portfolio',
      name: 'Trading Portfolio',
      description: 'View your forex trading performance',
      icon: '📈',
      examples: ['Show my portfolio', "What's my current P/L?"],
      category: 'Finance',
      voice_triggers: ['my portfolio', 'trading']
    },
    {
      id: 'youtube',
      name: 'YouTube Videos',
      description: 'Find and play YouTube videos',
      icon: '▶️',
      examples: ['Play the latest Sauti Sol video', 'Find Python tutorials'],
      category: 'Entertainment',
      voice_triggers: ['play video', 'youtube']
    },
    {
      id: 'generate_chart',
      name: 'Create Charts',
      description: 'Generate beautiful data visualizations',
      icon: '📊',
      examples: ['Create a bar chart', 'Visualize this data'],
      category: 'Productivity',
      voice_triggers: ['create chart', 'make graph']
    },
    {
      id: 'presentation',
      name: 'Create Presentations',
      description: 'Generate professional PowerPoint presentations',
      icon: '📽️',
      examples: ['Create a presentation about AI in healthcare', 'Make a pitch deck for my startup'],
      category: 'Productivity',
      voice_triggers: ['create presentation', 'make slides', 'powerpoint']
    },
    {
      id: 'themes',
      name: 'Themes',
      description: 'Customize colors and appearance',
      icon: '🎨',
      examples: ['Change theme', 'Switch to dark mode'],
      category: 'Settings',
      voice_triggers: ['change theme', 'appearance', 'colors'],
      action: () => setAppearanceModalOpen(true)
    },
  ]

  const [abilities, setAbilities] = useState<Ability[]>(FALLBACK_ABILITIES)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)

  // Fetch abilities from API
  useEffect(() => {
    const fetchAbilities = async () => {
      try {
        const response = await fetch('/api/v1/abilities/featured')
        if (response.ok) {
          const data = await response.json()
          if (data.length > 0) {
            setAbilities(data)
          }
        }
      } catch (error) {
        console.log('Using fallback abilities')
      }
    }
    fetchAbilities()
  }, [])

  // Auto-rotate abilities
  useEffect(() => {
    if (isExpanded) return // Don't rotate when expanded

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % abilities.length)
    }, 4000)

    return () => clearInterval(interval)
  }, [abilities.length, isExpanded])

  const currentAbility = abilities[currentIndex]

  const handleTryExample = (example: string, ability?: Ability) => {
    // If ability has a direct action, execute it
    if (ability?.action) {
      ability.action()
      return
    }
    if (onTryAbility) {
      onTryAbility(example)
    }
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Compact rotating view */}
      <AnimatePresence mode="wait">
        {!isExpanded && (
          <motion.div
            key={currentAbility.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="glass-card p-4 cursor-pointer hover:bg-white/10 transition-colors"
            onClick={() => setIsExpanded(true)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{currentAbility.icon}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">{currentAbility.name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary-light">
                      {currentAbility.category}
                    </span>
                  </div>
                  <p className="text-sm text-white/60">{currentAbility.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/40">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs">{currentIndex + 1}/{abilities.length}</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>

            {/* Example chips */}
            <div className="flex flex-wrap gap-2 mt-3">
              {currentAbility.examples.slice(0, 2).map((example, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleTryExample(example, currentAbility)
                  }}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/70 hover:bg-primary/20 hover:text-white transition-colors"
                >
                  "{example}"
                </button>
              ))}
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mt-3">
              {abilities.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation()
                    setCurrentIndex(idx)
                  }}
                  className={cn(
                    'w-1.5 h-1.5 rounded-full transition-all',
                    idx === currentIndex
                      ? 'w-4 bg-primary'
                      : 'bg-white/20 hover:bg-white/40'
                  )}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded grid view */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between px-2">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Niki's Abilities
              </h2>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Show less
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {abilities.map((ability, idx) => (
                <motion.div
                  key={ability.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass-card p-3 hover:bg-white/10 transition-colors cursor-pointer group"
                  onClick={() => handleTryExample(ability.examples[0], ability)}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xl">{ability.icon}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white text-sm truncate">
                        {ability.name}
                      </h3>
                      <p className="text-xs text-white/50 line-clamp-2">
                        {ability.description}
                      </p>
                    </div>
                  </div>

                  {/* Voice hint */}
                  <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-primary-light">
                      🎤 Say: "{ability.voice_triggers[0]}..."
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Voice tip */}
            <div className="glass-card p-3 bg-primary/10 border border-primary/20">
              <p className="text-sm text-white/80 text-center">
                💡 <span className="text-primary-light font-medium">Pro tip:</span> Just speak naturally!
                Say things like "Get me an Uber to the airport" or "Buy me some airtime"
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Mini version for sidebar or header
export function AbilitiesMini({ className }: { className?: string }) {
  const [currentIdx, setCurrentIdx] = useState(0)
  const abilities = [
    { id: 'uber', icon: '🚗', name: 'book Uber' },
    { id: 'airtime', icon: '📱', name: 'buy airtime' },
    { id: 'movies', icon: '🎬', name: 'find movies' },
    { id: 'themes', icon: '🎨', name: 'change themes' },
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % abilities.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [abilities.length])

  const current = abilities[currentIdx]

  return (
    <motion.div
      key={current.id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn('flex items-center gap-2 text-sm text-white/60', className)}
    >
      <span>{current.icon}</span>
      <span>I can {current.name}</span>
    </motion.div>
  )
}
